import moment from "moment";
import type { KeyValuePairsProps } from "@amzn/awsui-components-console";

import type { ProcessedApiResponse } from "src/api/types";

// ---------------------------------------------------------------------------
// claimOutputDisplay.ts
//
// Formats the `claimOutput` returned by `/createClaim` for display on the
// dynamic-form confirmation screen.
//
// `claimOutput` is an untyped Smithy `Document`, so the UI cannot rely on a
// fixed field list. `schemaClaimsService.documentToResponseFields` flattens
// whatever the backend returns into `ResponseField[]` keeping the raw keys
// (for example `claimNumber`). These helpers turn those raw keys into the
// human-readable labels the typed LOB flows hardcode in their API layer
// (see `src/api/claims/createWcClaim.ts`).
// ---------------------------------------------------------------------------

/** Timestamp-bearing keys whose epoch/ISO values are formatted for display. */
const TIMESTAMP_KEY_PATTERN = /(timestamp|createdat|updatedat)$/i;

/** Matches the same format the typed WC/Cargo/IPL flows render. */
const TIMESTAMP_DISPLAY_FORMAT = "MMMM Do YYYY, h:mm:ss a";

/** Words that read as acronyms once title-cased. */
const ACRONYMS: Record<string, string> = {
  id: "ID",
  url: "URL",
  tpa: "TPA",
  lob: "LOB",
  sim: "SIM",
};

/** Placeholder for fields the backend returned without a value. */
const EMPTY_VALUE = "-";

/**
 * Converts a raw `claimOutput` key into a display label.
 *
 * `claimNumber` -> `Claim Number`, `associateEmployeeId` -> `Associate
 * Employee ID`, `created_at_timestamp` -> `Created At Timestamp`. Keys that
 * are already spaced or title-cased pass through unchanged.
 */
export function formatClaimOutputLabel(fieldName: string): string {
  const spaced = fieldName
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();

  if (!spaced) {
    return fieldName;
  }

  return spaced
    .split(/\s+/)
    .map((word) => {
      const acronym = ACRONYMS[word.toLowerCase()];
      if (acronym) {
        return acronym;
      }
      // Leave all-caps words (already acronyms) alone.
      if (word === word.toUpperCase()) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

/**
 * Formats a `claimOutput` value for display. Timestamp-bearing fields are
 * rendered in the same long form the typed LOB flows use; everything else is
 * shown as returned. Values that fail to parse fall back to the raw string so
 * the user always sees what the backend sent.
 */
export function formatClaimOutputValue(fieldName: string, fieldValue: string | undefined): string {
  if (fieldValue === undefined || fieldValue === "") {
    return EMPTY_VALUE;
  }

  if (!TIMESTAMP_KEY_PATTERN.test(fieldName)) {
    return fieldValue;
  }

  const parsed = /^\d+$/.test(fieldValue)
    ? moment(Number(fieldValue))
    : moment(fieldValue, moment.ISO_8601, true);

  return parsed.isValid() ? parsed.format(TIMESTAMP_DISPLAY_FORMAT) : fieldValue;
}

/**
 * Builds the `KeyValuePairs` items for the "Claim Information" container from
 * a `/createClaim` response. Returns an empty list when the backend returned
 * no `claimOutput` fields so the caller can render the container empty rather
 * than crash.
 */
export function buildClaimOutputPairs(
  response: ProcessedApiResponse | null
): KeyValuePairsProps.Pair[] {
  if (!response?.data?.length) {
    return [];
  }

  return response.data.map((field) => ({
    label: formatClaimOutputLabel(field.fieldName),
    value: formatClaimOutputValue(field.fieldName, field.fieldValue),
  }));
}
